import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Separator } from '../ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import {
  User,
  Lock,
  Shield,
  Bell,
  Key,
  Mail,
  Smartphone,
  Eye,
  EyeOff,
  Save,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Copy,
  QrCode
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OwnerAdminSettings() {
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Profile state
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    role: '',
    created_at: null,
    last_login: null
  });
  const [editName, setEditName] = useState('');
  
  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  
  // MFA state
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaSetupDialog, setMfaSetupDialog] = useState(false);
  const [mfaSecret, setMfaSecret] = useState('');
  const [mfaQrCode, setMfaQrCode] = useState('');
  const [mfaVerifyCode, setMfaVerifyCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  
  // Notification preferences
  const [notifications, setNotifications] = useState({
    email_new_ticket: true,
    email_ticket_assigned: true,
    email_ticket_urgent: true,
    email_daily_summary: false,
    email_weekly_report: true
  });

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/settings/profile`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setEditName(data.name || '');
        setMfaEnabled(data.mfa_enabled || false);
        if (data.notifications) {
          setNotifications(prev => ({ ...prev, ...data.notifications }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleUpdateProfile = async () => {
    if (!editName.trim()) {
      toast.error('Name is required');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/settings/profile`, {
        credentials: 'include',
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editName })
      });
      
      if (response.ok) {
        toast.success('Profile updated');
        setProfile(prev => ({ ...prev, name: editName }));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update profile');
      }
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('All password fields are required');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/settings/password`, {
        credentials: 'include',
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });
      
      if (response.ok) {
        toast.success('Password changed successfully');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to change password');
      }
    } catch (error) {
      toast.error('Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const handleSetupMfa = async () => {
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/settings/mfa/setup`, {
        credentials: 'include',
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        setMfaSecret(data.secret);
        setMfaQrCode(data.qr_code);
        setMfaSetupDialog(true);
      } else {
        toast.error('Failed to initialize MFA setup');
      }
    } catch (error) {
      toast.error('Failed to setup MFA');
    }
  };

  const handleVerifyMfa = async () => {
    if (!mfaVerifyCode || mfaVerifyCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/settings/mfa/verify`, {
        credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: mfaVerifyCode })
      });
      
      if (response.ok) {
        const data = await response.json();
        setBackupCodes(data.backup_codes || []);
        setShowBackupCodes(true);
        setMfaEnabled(true);
        setMfaSetupDialog(false);
        setMfaVerifyCode('');
        toast.success('MFA enabled successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Invalid verification code');
      }
    } catch (error) {
      toast.error('Failed to verify MFA');
    } finally {
      setSaving(false);
    }
  };

  const handleDisableMfa = async () => {
    if (!confirm('Are you sure you want to disable MFA? This will make your account less secure.')) {
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/settings/mfa/disable`, {
        credentials: 'include',
        method: 'POST'
      });
      
      if (response.ok) {
        setMfaEnabled(false);
        toast.success('MFA disabled');
      } else {
        toast.error('Failed to disable MFA');
      }
    } catch (error) {
      toast.error('Failed to disable MFA');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateNotifications = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/settings/notifications`, {
        credentials: 'include',
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(notifications)
      });
      
      if (response.ok) {
        toast.success('Notification preferences saved');
      } else {
        toast.error('Failed to save preferences');
      }
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleBadge = (role) => {
    const colors = {
      owner: 'bg-amber-500/20 text-amber-400',
      super_admin: 'bg-violet-500/20 text-violet-400',
      support_manager: 'bg-blue-500/20 text-blue-400',
      support_agent: 'bg-cyan-500/20 text-cyan-400'
    };
    return (
      <Badge className={colors[role] || 'bg-muted0/20 text-muted-foreground'}>
        {role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Settings</h1>
          <p className="text-muted-foreground">Manage your account and preferences</p>
        </div>
        <Button variant="outline" onClick={fetchProfile} className="border-white/20 text-muted-foreground/80">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-card border border-white/15">
          <TabsTrigger value="profile" className="data-[state=active]:bg-white/10">
            <User className="h-4 w-4 mr-2" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="security" className="data-[state=active]:bg-white/10">
            <Shield className="h-4 w-4 mr-2" />
            Security
          </TabsTrigger>
          <TabsTrigger value="notifications" className="data-[state=active]:bg-white/10">
            <Bell className="h-4 w-4 mr-2" />
            Notifications
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="mt-6 space-y-6">
          <Card className="bg-card border-white/15">
            <CardHeader>
              <CardTitle className="text-white">Profile Information</CardTitle>
              <CardDescription className="text-muted-foreground">
                Update your account information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Name</label>
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Email</label>
                  <Input
                    value={profile.email}
                    disabled
                    className="bg-white/[0.07] border-white/20 text-muted-foreground"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p>
                </div>
              </div>
              
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Role</label>
                  <div className="pt-2">{getRoleBadge(profile.role)}</div>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Member Since</label>
                  <p className="text-white pt-2">{formatDate(profile.created_at)}</p>
                </div>
              </div>
              
              <div className="flex justify-end">
                <Button
                  onClick={handleUpdateProfile}
                  disabled={saving || editName === profile.name}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-white/15">
            <CardHeader>
              <CardTitle className="text-white">Session Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <p className="text-sm text-muted-foreground">Last Login</p>
                  <p className="text-white">{formatDate(profile.last_login)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Account Status</p>
                  <Badge className="bg-emerald-500/20 text-emerald-400 mt-1">Active</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="mt-6 space-y-6">
          {/* Password Change */}
          <Card className="bg-card border-white/15">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Change Password
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Update your password regularly for security
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Current Password</label>
                <div className="relative">
                  <Input
                    type={showPasswords ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="bg-white/10 border-white/20 text-white pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(!showPasswords)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  >
                    {showPasswords ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">New Password</label>
                  <Input
                    type={showPasswords ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Confirm New Password</label>
                  <Input
                    type={showPasswords ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  onClick={handleChangePassword}
                  disabled={saving || !currentPassword || !newPassword || !confirmPassword}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Key className="h-4 w-4 mr-2" />
                  {saving ? 'Changing...' : 'Change Password'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* MFA Setup */}
          <Card className="bg-card border-white/15">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Smartphone className="h-5 w-5" />
                Two-Factor Authentication (MFA)
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Add an extra layer of security to your account
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {mfaEnabled ? (
                    <div className="p-3 rounded-full bg-emerald-500/10">
                      <CheckCircle2 className="h-6 w-6 text-emerald-400" />
                    </div>
                  ) : (
                    <div className="p-3 rounded-full bg-yellow-500/10">
                      <AlertTriangle className="h-6 w-6 text-yellow-400" />
                    </div>
                  )}
                  <div>
                    <p className="text-white font-medium">
                      {mfaEnabled ? 'MFA Enabled' : 'MFA Not Enabled'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {mfaEnabled 
                        ? 'Your account is protected with two-factor authentication'
                        : 'Enable MFA to add extra security to your account'}
                    </p>
                  </div>
                </div>
                {mfaEnabled ? (
                  <Button 
                    variant="outline" 
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    onClick={handleDisableMfa}
                  >
                    Disable MFA
                  </Button>
                ) : (
                  <Button 
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={handleSetupMfa}
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    Enable MFA
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="mt-6">
          <Card className="bg-card border-white/15">
            <CardHeader>
              <CardTitle className="text-white">Email Notifications</CardTitle>
              <CardDescription className="text-muted-foreground">
                Configure which notifications you receive
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white">New Ticket Created</p>
                    <p className="text-sm text-muted-foreground">Get notified when a new support ticket is created</p>
                  </div>
                  <Switch
                    checked={notifications.email_new_ticket}
                    onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, email_new_ticket: checked }))}
                  />
                </div>
                
                <Separator className="bg-white/10" />
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white">Ticket Assigned to You</p>
                    <p className="text-sm text-muted-foreground">Get notified when a ticket is assigned to you</p>
                  </div>
                  <Switch
                    checked={notifications.email_ticket_assigned}
                    onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, email_ticket_assigned: checked }))}
                  />
                </div>
                
                <Separator className="bg-white/10" />
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white">Urgent Tickets</p>
                    <p className="text-sm text-muted-foreground">Get immediate alerts for urgent priority tickets</p>
                  </div>
                  <Switch
                    checked={notifications.email_ticket_urgent}
                    onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, email_ticket_urgent: checked }))}
                  />
                </div>
                
                <Separator className="bg-white/10" />
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white">Daily Summary</p>
                    <p className="text-sm text-muted-foreground">Receive a daily summary of open tickets</p>
                  </div>
                  <Switch
                    checked={notifications.email_daily_summary}
                    onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, email_daily_summary: checked }))}
                  />
                </div>
                
                <Separator className="bg-white/10" />
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white">Weekly Report</p>
                    <p className="text-sm text-muted-foreground">Receive a weekly performance report</p>
                  </div>
                  <Switch
                    checked={notifications.email_weekly_report}
                    onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, email_weekly_report: checked }))}
                  />
                </div>
              </div>
              
              <div className="flex justify-end pt-4">
                <Button
                  onClick={handleUpdateNotifications}
                  disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Preferences'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* MFA Setup Dialog */}
      <Dialog open={mfaSetupDialog} onOpenChange={setMfaSetupDialog}>
        <DialogContent className="bg-card border-white/15 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Setup Two-Factor Authentication</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Scan the QR code with your authenticator app
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {mfaQrCode && (
              <div className="flex justify-center">
                <div className="p-4 bg-white rounded-lg">
                  <img src={mfaQrCode} alt="MFA QR Code" className="w-48 h-48" />
                </div>
              </div>
            )}
            
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-2">Or enter this code manually:</p>
              <div className="flex items-center justify-center gap-2">
                <code className="px-3 py-2 bg-white/10 rounded text-white font-mono text-sm">
                  {mfaSecret}
                </code>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => copyToClipboard(mfaSecret)}
                >
                  <Copy className="h-4 w-4 text-muted-foreground" />
                </Button>
              </div>
            </div>
            
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Enter verification code</label>
              <Input
                placeholder="000000"
                value={mfaVerifyCode}
                onChange={(e) => setMfaVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="bg-white/10 border-white/20 text-white text-center text-2xl tracking-widest"
                maxLength={6}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMfaSetupDialog(false)}>Cancel</Button>
            <Button
              onClick={handleVerifyMfa}
              disabled={saving || mfaVerifyCode.length !== 6}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {saving ? 'Verifying...' : 'Verify & Enable'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backup Codes Dialog */}
      <Dialog open={showBackupCodes} onOpenChange={setShowBackupCodes}>
        <DialogContent className="bg-card border-white/15 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Backup Codes</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Save these codes in a secure location. Each code can only be used once.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="grid grid-cols-2 gap-2">
              {backupCodes.map((code, index) => (
                <code key={index} className="px-3 py-2 bg-white/10 rounded text-white font-mono text-sm text-center">
                  {code}
                </code>
              ))}
            </div>
            <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <p className="text-sm text-yellow-200">
                <AlertTriangle className="h-4 w-4 inline mr-2" />
                Store these codes safely. You will not be able to see them again.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => copyToClipboard(backupCodes.join('\n'))}
              variant="outline"
              className="border-white/20"
            >
              <Copy className="h-4 w-4 mr-2" />
              Copy All
            </Button>
            <Button
              onClick={() => setShowBackupCodes(false)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
